// Web shim for react-native-fast-image
import React from 'react';
import { Image as RNImage, ImageProps } from 'react-native';

// 类型定义
export type OnLoadEvent = {
  nativeEvent: {
    width: number;
    height: number;
  };
};

export type OnProgressEvent = {
  nativeEvent: {
    loaded: number;
    total: number;
  };
};

export type Priority = 'low' | 'normal' | 'high';

export type ResizeMode = 'contain' | 'cover' | 'stretch' | 'center';

export type Source = {
  uri?: string;
  headers?: { [key: string]: string };
  priority?: Priority;
  cache?: 'immutable' | 'web' | 'cacheOnly';
};

export interface FastImageProps extends Omit<ImageProps, 'source'> {
  source: Source | Source[] | number;
  resizeMode?: ResizeMode;
  onLoadStart?: () => void;
  onProgress?: (event: OnProgressEvent) => void;
  onLoad?: (event: OnLoadEvent) => void;
  onError?: () => void;
  onLoadEnd?: () => void;
}

const FastImage = React.forwardRef<RNImage, FastImageProps>((props, ref) => {
  const { source, resizeMode, onLoad, onLoadStart, onProgress, onError, onLoadEnd, ...restProps } = props;
  
  // 转换 source 格式
  let imageSource: any = source;
  if (typeof source === 'object' && !Array.isArray(source) && 'uri' in source) {
    imageSource = { uri: source.uri };
  } else if (Array.isArray(source) && source.length > 0) {
    imageSource = typeof source[0] === 'object' && 'uri' in source[0] 
      ? { uri: source[0].uri } 
      : source[0];
  }

  // 处理加载事件
  const handleLoad = (event: any) => {
    if (onLoad) {
      onLoad({
        nativeEvent: {
          width: event.nativeEvent?.width || 0,
          height: event.nativeEvent?.height || 0,
        },
      });
    }
    if (onLoadEnd) {
      onLoadEnd();
    }
  };

  const handleLoadStart = () => {
    if (onLoadStart) {
      onLoadStart();
    }
  };

  const handleError = () => {
    if (onError) {
      onError();
    }
    if (onLoadEnd) {
      onLoadEnd();
    }
  };

  return (
    <RNImage
      ref={ref}
      source={imageSource}
      resizeMode={resizeMode}
      onLoad={handleLoad}
      onLoadStart={handleLoadStart}
      onError={handleError}
      {...restProps}
    />
  );
});

FastImage.displayName = 'FastImage';

FastImage.resizeMode = {
  contain: 'contain' as ResizeMode,
  cover: 'cover' as ResizeMode,
  stretch: 'stretch' as ResizeMode,
  center: 'center' as ResizeMode,
};

FastImage.priority = {
  low: 'low' as Priority,
  normal: 'normal' as Priority,
  high: 'high' as Priority,
};

FastImage.cacheControl = {
  immutable: 'immutable',
  web: 'web',
  cacheOnly: 'cacheOnly',
};

// 添加静态方法以匹配 react-native-fast-image 的 API
FastImage.preload = (sources: Source[]): Promise<void> => {
  console.log('FastImage.preload called (Web):', sources);
  // Web 环境下，图片预加载由浏览器自动处理
  return Promise.resolve();
};

FastImage.clearMemoryCache = (): Promise<void> => {
  console.log('FastImage.clearMemoryCache called (Web - no-op)');
  // Web 环境下，无法直接清除浏览器内存缓存
  return Promise.resolve();
};

FastImage.clearDiskCache = (): Promise<void> => {
  console.log('FastImage.clearDiskCache called (Web - no-op)');
  // Web 环境下，无法直接清除浏览器磁盘缓存
  return Promise.resolve();
};

export default FastImage;

